import { PlanDetailsModel, CurrencyEnum } from '../interfaces/plan.interface';
import { getCurrencyForCountry, SUPPORTED_CURRENCIES } from '../config/currencies';

export interface CurrencyDisplayInfo {
  currency: CurrencyEnum;
  symbol: string;
  cost: number;
  displayPrice: string;
}

/**
 * Get the appropriate currency and pricing for a plan based on user's country
 */
export const getPlanCurrencyDisplay = (
  plan: PlanDetailsModel,
  userCountry?: string
): CurrencyDisplayInfo => {
  // First check if backend has provided currency-specific display fields
  if (plan.displayCurrency && plan.displayCost !== undefined) {
    const currencyConfig = SUPPORTED_CURRENCIES[plan.displayCurrency];
    return {
      currency: plan.displayCurrency,
      symbol: currencyConfig.symbol,
      cost: plan.displayCost,
      displayPrice: `${currencyConfig.symbol}${(+plan.displayCost || 0).toFixed(2)}`
    };
  }
  
  // Fallback to frontend currency detection
  const currency = userCountry ? getCurrencyForCountry(userCountry) || CurrencyEnum.USD : CurrencyEnum.USD;
  const currencyConfig = SUPPORTED_CURRENCIES[currency];
  
  // Get pricing for the determined currency
  let cost = 0;
  
  if (plan.pricing && plan.pricing.length > 0) {
    // Find pricing for the specific currency
    const currencyPricing = plan.pricing.find(p => p.currency === currency);
    if (currencyPricing) {
      cost = currencyPricing.cost;
    } else {
      // Fallback to default pricing (USD)
      const defaultPricing = plan.pricing.find(p => p.isDefault) || plan.pricing[0];
      cost = defaultPricing?.cost || 0;
    }
  } else {
    // Fallback to legacy cost field (assumed USD)
    cost = parseFloat(plan.cost) || 0;
  }
  
  return {
    currency,
    symbol: currencyConfig.symbol,
    cost,
    displayPrice: `${currencyConfig.symbol}${(+cost || 0).toFixed(2)}`
  };
};

/**
 * Get all available currencies for a plan
 */
export const getPlanAvailableCurrencies = (plan: PlanDetailsModel): CurrencyDisplayInfo[] => {
  const currencies: CurrencyDisplayInfo[] = [];
  
  if (plan.pricing && plan.pricing.length > 0) {
    // Use pricing from plan
    plan.pricing.forEach(pricing => {
      const currencyConfig = SUPPORTED_CURRENCIES[pricing.currency];
      currencies.push({
        currency: pricing.currency,
        symbol: currencyConfig.symbol,
        cost: pricing.cost,
        displayPrice: `${currencyConfig.symbol}${(+pricing.cost || 0).toFixed(2)}`
      });
    });
  } else {
    // Fallback to USD only
    const currencyConfig = SUPPORTED_CURRENCIES[CurrencyEnum.USD];
    currencies.push({
      currency: CurrencyEnum.USD,
      symbol: currencyConfig.symbol,
      cost: parseFloat(plan.cost) || 0,
      displayPrice: `${currencyConfig.symbol}${(parseFloat(plan.cost) || 0).toFixed(2)}`
    });
  }
  
  return currencies;
}; 